import React from "react";
import { useGoogleLogin, useGoogleOAuth } from "@react-oauth/google";
import { Button } from "./ui/button";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// `window.location.origin` is what we both send to Google AND what the backend
// passes back to Google's token endpoint — they MUST match exactly. Hardcoding
// or env-var-ing this URL breaks OAuth on preview/prod swaps.
//
// Feb 2026 crash-fix: If the production build was made without
// REACT_APP_GOOGLE_CLIENT_ID in the env, `<GoogleOAuthProvider clientId="">`
// receives an empty string. `useGoogleLogin` then unconditionally calls
// google.accounts.oauth2.initCodeClient({client_id: ""}) inside a useEffect,
// which throws "Missing required parameter client_id." The uncaught throw
// unmounts the whole React tree — that's what caused the entirely-blank
// /login screen on syllox.in.
//
// Fix: split into two components. When `clientId` is missing, render an
// inert placeholder button that does NOT call `useGoogleLogin` — email/pw
// login keeps working and the page stays alive. Hook rules are respected
// because each component either always-or-never calls the hook.
function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

const BASE_CLS = "w-full bg-white text-zinc-900 hover:bg-zinc-50 border-zinc-300 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100";

export default function GoogleSignInButton({ label = "Continue with Google", testId = "google-signin-button" }) {
  // Read clientId from context set by <GoogleOAuthProvider> in App.js.
  // If it's empty, the parent build was made without REACT_APP_GOOGLE_CLIENT_ID
  // — render an inert placeholder instead of wiring the crashy GSI hook.
  const { clientId } = useGoogleOAuth();
  return clientId
    ? <ConfiguredButton label={label} testId={testId} />
    : <InertButton label={label} testId={testId} />;
}

// The real button — only used when we actually have a client_id.
function ConfiguredButton({ label, testId }) {
  // We use redirect flow (not popup) so behavior is identical on mobile,
  // desktop, and standalone PWA installs. Google redirects back to our
  // /auth/google/callback frontend route, which posts the code to the API.
  // Default scopes from useGoogleLogin already cover openid+email+profile.
  const login = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "redirect",
    redirect_uri: window.location.origin + "/auth/google/callback",
  });
  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => login()}
      data-testid={testId}
      className={BASE_CLS}
    >
      <span className="mr-2 inline-flex items-center justify-center"><GoogleIcon size={18}/></span>
      {label}
    </Button>
  );
}

// Fallback shown when REACT_APP_GOOGLE_CLIENT_ID isn't baked into the build.
// Keeps the login page rendering + gives operators a clear next step.
function InertButton({ label, testId }) {
  const onClick = () => {
    toast.error(
      "Google Sign-In isn't configured for this deployment. Please sign in with email + password instead."
    );
  };
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      data-testid={testId}
      aria-disabled="true"
      className={`${BASE_CLS} opacity-70`}
    >
      <span className="mr-2 inline-flex items-center justify-center"><GoogleIcon size={18}/></span>
      {label}
    </Button>
  );
}
